import { useMemo, useState } from 'react';
import styled from 'styled-components';

import { ColumnDef, createColumnHelper, Row, SortingState } from '@tanstack/react-table';

import { Dimensions, Sources } from 'components/insights/constants';
import TableHeader from 'components/insights/layout/details/TableHeader';
import TableTitle from 'components/insights/layout/details/TableTitle';
import useAnalytics from 'components/insights/query/useAnalytics';
import DataTable from 'components/shared/DataTable';
import ExpandableArea from 'components/shared/ExpandableArea';
import { simpleDate } from 'lib/date';
import { Analytics } from 'services/api/insights/analytics';
import { serializeSort } from 'stores/helpers';
import { useInsightsStore } from 'stores/insightsStore';
import ChevronRight from 'svg/ChevronRight';

import ApiExport from './ApiExport';
import { DrillableItem } from './drillableTableComponents';
import MetricDetailBreadcrumbs from './MetricDetailBreadcrumbs';
import TableContainer from './TableContainer';

type BasePatientRow = {
  episodeUrl: string;
  patientName: string;
  groupName: string;
};

type ThirdColumnConfig<T> = {
  dimension: Dimensions;
  header: string;
  renderCell: (value: T, isRowHovered: boolean) => React.ReactNode;
  parseValue: (rawValue: any) => T;
};

type PatientDrillInTableProps<T> = {
  source: Sources;
  title: string;
  dataCy: string;
  exportFilename: string;
  onSelectedDimensionsChange: (selectedDimensions: Dimensions[]) => void;
  thirdColumn: ThirdColumnConfig<T>;
};

const PatientDrillInTable = <T,>({
  source,
  title,
  dataCy,
  exportFilename,
  onSelectedDimensionsChange,
  thirdColumn,
}: PatientDrillInTableProps<T>) => {
  const startDate = useInsightsStore((state) => state.filters.startDate);
  const endDate = useInsightsStore((state) => state.filters.endDate);
  const selectedGroupType = useInsightsStore((state) => state.selectedGroupType);

  const [hoveredRowId, setHoveredRowId] = useState('');
  const [sorting, setSorting] = useState<SortingState>([{ id: Dimensions.PATIENT_NAME, desc: false }]);

  const sortBy = useMemo(() => serializeSort(sorting), [sorting]);

  const queryParams = useMemo(
    () => ({
      source: source,
      dimensions: [Dimensions.PATIENT_NAME, Dimensions.EPISODE_URL, Dimensions.GROUP_NAME, thirdColumn.dimension],
      metrics: [],
      sortBy,
      pageSize: 10,
    }),
    [thirdColumn.dimension, source, sortBy]
  );

  const analyticsResponse = useAnalytics(
    queryParams,
    { onIntersecting: true },
    {
      initialPageParam: queryParams,
      getNextPageParam: (lastPage: Analytics) => lastPage?.links?.next?.payload,
    }
  );

  const { data, query, customRef } = analyticsResponse;

  type TableRow = BasePatientRow & { thirdColumnValue: T };

  const tableData = useMemo<TableRow[]>(() => {
    return (
      data?.dimensions?.currentSeries?.map((row) => {
        const [patientName, episodeUrl, groupName, thirdColumnRaw] = row;
        return {
          episodeUrl: episodeUrl || '',
          patientName: patientName || '',
          groupName: groupName || '',
          thirdColumnValue: thirdColumn.parseValue(thirdColumnRaw),
        };
      }) || []
    );
  }, [data, thirdColumn]);

  const columnHelper = useMemo(() => createColumnHelper<TableRow>(), []);
  const columns = useMemo<ColumnDef<TableRow, any>[]>(
    () => [
      columnHelper.accessor('patientName', {
        id: Dimensions.PATIENT_NAME,
        header: 'Patient Name',
        cell: (data) => (
          <DrillableItem drillable isRowHovered={hoveredRowId === data.row.id}>
            {data.getValue()}
          </DrillableItem>
        ),
      }),
      columnHelper.accessor('groupName', {
        id: Dimensions.GROUP_NAME,
        header: selectedGroupType.name,
        cell: (data) => data.getValue() || <>&mdash;</>,
      }),
      columnHelper.accessor('thirdColumnValue', {
        id: thirdColumn.dimension,
        header: thirdColumn.header,
        cell: (data) => (
          <ThirdColumnCellContainer>
            {thirdColumn.renderCell(data.getValue(), hoveredRowId === data.row.id)}
            {hoveredRowId === data.row.id && <StyledChevron width={9} height={9} />}
          </ThirdColumnCellContainer>
        ),
      }),
    ],
    [columnHelper, selectedGroupType.name, hoveredRowId, thirdColumn]
  );

  const handleRowClick = (row: Row<TableRow>) => {
    const episodeUrl = row.original.episodeUrl;

    if (episodeUrl) {
      window.open(episodeUrl, '_blank');
    }
  };

  const desktopOnlyColumns = [Dimensions.GROUP_NAME, thirdColumn.dimension];

  return (
    <StyledTableContainer data-cy={dataCy}>
      <StyledTableHeader>
        <TableHeader.Row>
          <TableTitle>
            {title} ({simpleDate(startDate as Date)} - {simpleDate(endDate as Date)})
          </TableTitle>
          <ApiExport
            disabled={!tableData.length}
            show={!query.isLoading}
            params={queryParams}
            filename={exportFilename}
          />
        </TableHeader.Row>
        <MetricDetailBreadcrumbs onSelectedDimensionsChange={onSelectedDimensionsChange} />
      </StyledTableHeader>
      <ExpandableArea>
        <div ref={customRef}>
          <DataTable
            columns={columns}
            data={tableData}
            sorting={sorting}
            onSortingChange={setSorting}
            manualSorting
            desktopOnlyColumns={desktopOnlyColumns}
            hasMore={query.hasNextPage}
            loading={query.isLoading || query.isFetchingNextPage}
            setHoveredRowId={setHoveredRowId}
            onBodyEndReached={query.fetchNextPage}
            hasFooter={false}
            onRowClick={handleRowClick}
            drillable
          />
        </div>
      </ExpandableArea>
    </StyledTableContainer>
  );
};

const StyledTableContainer = styled(TableContainer)`
  min-height: auto;
  max-height: unset;
  padding: 0;
`;

const StyledTableHeader = styled(TableHeader)`
  padding: 24px 24px 0;
`;

const StyledChevron = styled(ChevronRight)`
  transform: translate(100%, 0);
`;

const ThirdColumnCellContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
`;

export default PatientDrillInTable;
